import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import api, { BACKEND_URL, formatDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { DownloadSimple, FilePdf } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export default function Downloads() {
  const { isMember } = useAuth();
  const [downloads, setDownloads] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isMember) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get("/downloads");
        setDownloads(data);
      } catch (err) {
        console.error("Failed to load downloads:", err);
      }
      setLoading(false);
    })();
  }, [isMember]);

  if (!isMember) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="downloads-gated">
          <div className="section-label mb-4">— RESTRICTED</div>
          <h1 className="font-chivo font-black text-4xl text-white uppercase mb-4">Download Center</h1>
          <p className="font-plex text-[#9CA3AF] mb-8">Activate your membership to access templates, agreements, safety docs, and more.</p>
          <Link to="/billing" className="btn-primary">Activate Membership</Link>
        </div>
      </Layout>
    );
  }

  const categories = ["All", ...Array.from(new Set(downloads.map((d) => d.category)))];
  const filtered = activeCat === "All" ? downloads : downloads.filter((d) => d.category === activeCat);

  const getDownloadHref = (d) => {
    if (d.external_url && d.external_url !== "#") return d.external_url;
    if (d.file_id) return `${BACKEND_URL}/api/files/${d.file_id}/download`;
    return "#";
  };

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-10">
        <div className="mb-8">
          <div className="section-label mb-3">— DOWNLOAD CENTER</div>
          <h1 className="font-chivo font-black text-4xl lg:text-5xl text-white uppercase tracking-tight" data-testid="downloads-headline">Documents & Templates</h1>
          <p className="font-plex text-[#9CA3AF] mt-3 max-w-2xl">Estimate templates, rental agreements, service forms, safety documents, marketing materials, equipment guides, and checklists.</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((c) => (
            <button key={c} onClick={() => setActiveCat(c)} className={`tag ${activeCat === c ? "tag-active" : ""}`} data-testid={`downloads-category-${c.toLowerCase()}`}>
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="font-mono text-xs text-[#52525B]">[LOADING]</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#262626] border border-[#262626]" data-testid="downloads-grid">
            {filtered.map((d) => (
              <div key={d.id} className="bg-[#0A0A0A] p-6 hover:bg-[#121212] transition-colors flex items-start gap-4" data-testid={`download-card-${d.id}`}>
                <FilePdf size={36} weight="duotone" className="text-[#00D4FF] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[10px] text-[#00D4FF] tracking-widest uppercase mb-1">{d.category}</div>
                  <div className="font-chivo font-bold text-white truncate">{d.title}</div>
                  <div className="font-plex text-xs text-[#9CA3AF] mt-1 line-clamp-2">{d.description}</div>
                  <div className="font-mono text-[10px] text-[#52525B] mt-2">{d.file_name} · Added {formatDate(d.created_at)}</div>
                  <a href={getDownloadHref(d)} target="_blank" rel="noreferrer" className="btn-secondary text-xs mt-3 py-2 px-3 inline-flex" data-testid={`download-btn-${d.id}`}>
                    <DownloadSimple size={14} weight="bold" /> Download
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
